import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, IconButton, Paper, Popper, Tooltip, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomInOutlined';
import ZoomOutIcon from '@mui/icons-material/ZoomOutOutlined';
import FitScreenIcon from '@mui/icons-material/FitScreenOutlined';
import MyLocationIcon from '@mui/icons-material/MyLocationOutlined';
import { brand } from '../../theme';
import { withAlpha } from '../../utils/annotationCategories';
import { formatDuration, formatFrameRate, formatTimecode, normalizeFps } from '../../utils/timecode';
import { studioTimeline } from '../AnnotationStudio/studioTokens';
import {
  DragMode,
  ZOOM_STEP,
  anchoredScrollLeft,
  applyDrag,
  assignRows,
  chooseTickSpacing,
  clampZoom,
  computeTicks,
  fitPixelsPerSecond,
  formatRulerLabel,
  pxToTime,
  snapTime,
} from './timelineMath';
import type {
  ProTimelineHandle,
  TimelineItem,
  TimelineItemChange,
  TimelineLane,
  TimelineLaneKind,
} from './timelineTypes';

export type { ProTimelineHandle, TimelineItem, TimelineItemChange, TimelineLane, TimelineLaneKind } from './timelineTypes';

export interface ProTimelineProps {
  /** Seconds. When unknown (0), the end of the last item is used. */
  duration: number;
  currentTime: number;
  playing?: boolean;
  lanes: TimelineLane[];
  fps?: number;
  selectedItemId?: string | null;
  /** In/out marks, shaded across every lane. */
  inPoint?: number | null;
  outPoint?: number | null;
  title?: React.ReactNode;
  /** Extra controls on the left of the toolbar, after the title. */
  toolbarExtras?: React.ReactNode;
  /** Shown instead of the lanes when there is nothing to place in time. */
  emptyMessage?: string;
  onSeek?: (seconds: number) => void;
  onItemSelect?: (item: TimelineItem, lane: TimelineLane) => void;
  /** Double-click (e.g. open an editor). */
  onItemActivate?: (item: TimelineItem, lane: TimelineLane) => void;
  onItemContextMenu?: (item: TimelineItem, lane: TimelineLane, event: React.MouseEvent) => void;
  /** Called once when a drag/resize/nudge ends. A rejected promise snaps the item back. */
  onItemChange?: (item: TimelineItem, lane: TimelineLane, next: TimelineItemChange) => void | Promise<unknown>;
  'data-testid'?: string;
}

const GUTTER_WIDTH = 140;
/** Narrower lane labels on phones, so the ruler keeps most of the width. */
const GUTTER_WIDTH_PHONE = 104;
const RULER_HEIGHT = 28;
const RIGHT_PAD = 24;
const LANE_PAD = 6;
const ROW_GAP = 3;
const MIN_LANE_HEIGHT = 36;
const ROW_HEIGHT: Record<TimelineLaneKind, number> = { markers: 22, tasks: 24, assets: 22 };
const DEFAULT_MAX_ROWS: Record<TimelineLaneKind, number> = { markers: 3, tasks: 6, assets: 4 };
const VIRTUAL_BUFFER_PX = 400;
const PLAYHEAD_COLOR = studioTimeline.playhead;
const DEFAULT_ITEM_COLOR = brand.slate;
const TIMECODE_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, monospace';
/** Width of one ruler-label character (10.5px monospace), to keep labels inside the ruler. */
const RULER_CHAR_PX = 6.5;

interface DragState {
  itemId: string;
  laneId: string;
  mode: DragMode;
  startX: number;
  original: { start: number; end: number };
  isPoint: boolean;
  preview: { start: number; end: number };
  moved: boolean;
}

interface Pending {
  start: number;
  end: number | null;
  at: number;
}

const itemEnd = (item: TimelineItem) =>
  typeof item.end === 'number' && Number.isFinite(item.end) && item.end > item.start ? item.end : null;

const describeRange = (item: TimelineItem, fps: number) => {
  const end = itemEnd(item);
  return end === null
    ? formatTimecode(item.start, fps)
    : `${formatTimecode(item.start, fps)} – ${formatTimecode(end, fps)} (${formatDuration(end - item.start)})`;
};

interface LaneTrackProps {
  lane: TimelineLane;
  kind: TimelineLaneKind;
  height: number;
  rows: number[];
  pps: number;
  viewStart: number;
  viewEnd: number;
  fps: number;
  selectedItemId: string | null;
  dragPreview: DragState | null;
  pending: Record<string, Pending>;
  canDrag: boolean;
  onItemPointerDown: (event: React.PointerEvent, item: TimelineItem, lane: TimelineLane, mode: DragMode) => void;
  onItemClick: (event: React.MouseEvent, item: TimelineItem, lane: TimelineLane) => void;
  onItemDoubleClick: (item: TimelineItem, lane: TimelineLane) => void;
  onItemContextMenu: (event: React.MouseEvent, item: TimelineItem, lane: TimelineLane) => void;
  onItemKeyDown: (event: React.KeyboardEvent, item: TimelineItem, lane: TimelineLane) => void;
  onItemHover: (item: TimelineItem | null, lane: TimelineLane | null, anchor: HTMLElement | null) => void;
  onBackgroundPointerDown: (event: React.PointerEvent) => void;
}

/** One lane's items. Memoised so playhead updates do not re-render hundreds of markers. */
const LaneTrack = memo(function LaneTrack({
  lane,
  kind,
  height,
  rows,
  pps,
  viewStart,
  viewEnd,
  fps,
  selectedItemId,
  dragPreview,
  pending,
  canDrag,
  onItemPointerDown,
  onItemClick,
  onItemDoubleClick,
  onItemContextMenu,
  onItemKeyDown,
  onItemHover,
  onBackgroundPointerDown,
}: LaneTrackProps) {
  const rowHeight = ROW_HEIGHT[kind];

  return (
    <Box
      role="group"
      aria-label={`${lane.label} lane`}
      data-testid={`timeline-lane-${lane.id}`}
      onPointerDown={onBackgroundPointerDown}
      sx={{
        position: 'relative',
        height,
        borderBottom: `1px solid ${brand.border}`,
        cursor: 'default',
      }}
    >
      {lane.items.length === 0 && lane.emptyText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ position: 'absolute', left: viewStart * pps + 12, top: '50%', transform: 'translateY(-50%)', whiteSpace: 'nowrap', pointerEvents: 'none' }}
        >
          {lane.emptyText}
        </Typography>
      )}
      {lane.items.map((item, index) => {
        const drag = dragPreview && dragPreview.itemId === item.id ? dragPreview : null;
        const isDragging = drag !== null;
        const override = pending[item.id];
        const start = drag ? drag.preview.start : override ? override.start : item.start;
        const rawEnd = drag
          ? drag.isPoint ? null : drag.preview.end
          : override ? override.end : itemEnd(item);
        const end = rawEnd !== null && rawEnd !== undefined && rawEnd > start ? rawEnd : null;
        const visibleEnd = end ?? start;
        if (visibleEnd < viewStart || start > viewEnd) return null;

        const color = item.color || DEFAULT_ITEM_COLOR;
        const row = rows[index] ?? 0;
        const top = LANE_PAD + row * (rowHeight + ROW_GAP);
        const x = start * pps;
        const isPoint = end === null;
        const width = isPoint ? 12 : Math.max(6, (end - start) * pps);
        const left = isPoint ? x - 6 : x;
        const selected = selectedItemId === item.id;
        const draggable = canDrag && item.editable !== false;
        const showLabel = !isPoint && width >= 34;
        const accessibleName = `${lane.label}: ${item.ariaLabel || item.label}, ${describeRange({ ...item, start, end }, fps)}`;

        let visual: React.ReactNode;
        let surface: Record<string, unknown>;
        if (isPoint) {
          surface = {};
          visual = (
            <>
              <Box sx={{ position: 'absolute', left: 5, top: 0, bottom: 0, width: 2, borderRadius: 1, bgcolor: color }} />
              <Box
                sx={{
                  position: 'absolute',
                  left: 1,
                  top: 1,
                  width: 10,
                  height: 10,
                  transform: 'rotate(45deg)',
                  borderRadius: '2px',
                  bgcolor: color,
                  boxShadow: `0 0 0 1.5px ${studioTimeline.markerRing}`,
                }}
              />
            </>
          );
        } else if (kind === 'tasks') {
          surface = { bgcolor: color, color: studioTimeline.onSolid, borderRadius: '5px', boxShadow: studioTimeline.barShadow };
        } else if (kind === 'assets') {
          // The colour is on the tint and the border; the label stays in ink (a
          // category colour on its own tint falls under 4.5:1).
          surface = { bgcolor: withAlpha(color, 0.12), color: 'text.primary', border: `1px solid ${withAlpha(color, 0.55)}`, borderRadius: '5px' };
        } else {
          surface = { bgcolor: withAlpha(color, 0.16), color: 'text.primary', borderLeft: `3px solid ${color}`, borderRadius: '3px' };
        }

        return (
          <Box
            key={item.id}
            role="button"
            tabIndex={0}
            aria-label={accessibleName}
            aria-pressed={selected}
            data-testid={`timeline-item-${item.id}`}
            data-selected={selected ? 'true' : 'false'}
            onPointerDown={(event) => {
              const handle = (event.target as HTMLElement).dataset?.handle as DragMode | undefined;
              onItemPointerDown(event, item, lane, handle || 'move');
            }}
            onClick={(event) => onItemClick(event, item, lane)}
            onDoubleClick={() => onItemDoubleClick(item, lane)}
            onContextMenu={(event) => onItemContextMenu(event, item, lane)}
            onKeyDown={(event) => onItemKeyDown(event, item, lane)}
            onPointerEnter={(event) => onItemHover(item, lane, event.currentTarget)}
            onPointerLeave={() => onItemHover(null, null, null)}
            onFocus={(event) => onItemHover(item, lane, event.currentTarget)}
            onBlur={() => onItemHover(null, null, null)}
            sx={{
              position: 'absolute',
              left,
              top,
              width,
              height: rowHeight,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: showLabel ? 0.75 : 0,
              overflow: isPoint ? 'visible' : 'hidden',
              cursor: draggable ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
              opacity: item.dimmed ? 0.55 : 1,
              zIndex: selected || isDragging ? 3 : 1,
              outline: selected ? `2px solid ${studioTimeline.selected}` : 'none',
              outlineOffset: 1,
              transition: isDragging ? 'none' : 'box-shadow 120ms ease, filter 120ms ease',
              touchAction: 'none',
              userSelect: 'none',
              '&:hover': { filter: 'brightness(1.06)', zIndex: 4 },
              '&:focus-visible': { outline: `2px solid ${studioTimeline.selected}`, outlineOffset: 2, zIndex: 4 },
              '&:hover [data-handle]': { opacity: 1 },
              ...surface,
            }}
          >
            {visual}
            {showLabel && item.icon && (
              <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0, '& svg': { fontSize: 14 } }}>
                {item.icon}
              </Box>
            )}
            {showLabel && (
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'inherit',
                  pointerEvents: 'none',
                }}
              >
                {item.label}
              </Typography>
            )}
            {draggable && !isPoint && (
              <>
                <Box
                  data-handle="resize-start"
                  aria-hidden="true"
                  sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize', opacity: 0, bgcolor: studioTimeline.handle }}
                />
                <Box
                  data-handle="resize-end"
                  aria-hidden="true"
                  sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize', opacity: 0, bgcolor: studioTimeline.handle }}
                />
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
});

/**
 * NLE-style timeline: an adaptive timecode ruler, zoom (buttons and
 * Ctrl/Cmd + wheel, anchored on the cursor), a playhead that follows playback,
 * click/drag scrubbing, and lanes of markers, spans, tasks and assets. Items
 * are focusable (Enter seeks to them) and, in editable lanes, can be dragged
 * and resized with snapping to frames, neighbouring edges and the playhead.
 */
const ProTimeline = forwardRef<ProTimelineHandle, ProTimelineProps>(function ProTimeline(
  {
    duration,
    currentTime,
    playing = false,
    lanes,
    fps: fpsProp,
    selectedItemId = null,
    inPoint = null,
    outPoint = null,
    title,
    toolbarExtras,
    emptyMessage,
    onSeek,
    onItemSelect,
    onItemActivate,
    onItemContextMenu,
    onItemChange,
    'data-testid': testId,
  },
  ref
) {
  const fps = normalizeFps(fpsProp);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(800);
  const [fitMode, setFitMode] = useState(true);
  const [zoomPps, setZoomPps] = useState(10);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [follow, setFollow] = useState(true);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [pending, setPending] = useState<Record<string, Pending>>({});
  const [hover, setHover] = useState<{ item: TimelineItem; lane: TimelineLane; anchor: HTMLElement } | null>(null);
  const pendingScrollRef = useRef<number | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const justDraggedRef = useRef(false);
  const scrubbingRef = useRef(false);
  const scrollFrameRef = useRef<number | null>(null);

  // Use the end of the last item when the media length is not known yet.
  const effectiveDuration = useMemo(() => {
    if (duration > 0 && Number.isFinite(duration)) return duration;
    let max = 0;
    for (const lane of lanes) {
      for (const item of lane.items) max = Math.max(max, itemEnd(item) ?? item.start);
    }
    return max > 0 ? max * 1.05 : 0;
  }, [duration, lanes]);

  const fitPps = fitPixelsPerSecond(effectiveDuration, Math.max(40, viewportWidth - RIGHT_PAD));
  const pps = fitMode ? fitPps : clampZoom(zoomPps, fitPps);
  const contentWidth = Math.max(viewportWidth, effectiveDuration * pps + RIGHT_PAD);

  // Latest values for listeners registered once.
  const live = useRef({ pps, fitPps, effectiveDuration, currentTime, scrollLeft, viewportWidth, onSeek, onItemChange, lanes, inPoint, outPoint });
  live.current = { pps, fitPps, effectiveDuration, currentTime, scrollLeft, viewportWidth, onSeek, onItemChange, lanes, inPoint, outPoint };

  // Measure the scroll viewport (jsdom has no ResizeObserver and reports 0).
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    const measure = () => {
      const width = scroller.clientWidth;
      if (width > 0) setViewportWidth(width);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  const zoomTo = useCallback((nextPps: number, anchorViewportX?: number) => {
    const scroller = scrollerRef.current;
    const { pps: current, fitPps: fit, currentTime: now, viewportWidth: width } = live.current;
    const clamped = clampZoom(nextPps, fit);
    const scrollPos = scroller?.scrollLeft ?? 0;
    const playheadX = now * current - scrollPos;
    const anchorX = anchorViewportX ?? (playheadX >= 0 && playheadX <= width ? playheadX : width / 2);
    const anchorTime = (scrollPos + anchorX) / current;
    pendingScrollRef.current = anchoredScrollLeft(anchorTime, anchorX, clamped);
    setFitMode(Math.abs(clamped - fit) < 1e-9);
    setZoomPps(clamped);
  }, []);

  const fit = useCallback(() => {
    pendingScrollRef.current = 0;
    setFitMode(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      zoomIn: () => zoomTo(live.current.pps * ZOOM_STEP),
      zoomOut: () => zoomTo(live.current.pps / ZOOM_STEP),
      fit,
    }),
    [zoomTo, fit]
  );

  // Apply the scroll position a zoom asked for once the new width is laid out.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller && pendingScrollRef.current !== null) {
      scroller.scrollLeft = pendingScrollRef.current;
      pendingScrollRef.current = null;
      setScrollLeft(scroller.scrollLeft);
    }
  }, [pps, fitMode]);

  // Ctrl/Cmd + wheel zooms around the cursor. Needs a non-passive listener to
  // stop the browser zooming the page.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      const rect = scroller.getBoundingClientRect();
      const factor = Math.exp(-event.deltaY * 0.0025);
      zoomTo(live.current.pps * factor, event.clientX - rect.left);
    };
    scroller.addEventListener('wheel', onWheel, { passive: false });
    return () => scroller.removeEventListener('wheel', onWheel);
  }, [zoomTo]);

  const handleScroll = () => {
    if (scrollFrameRef.current !== null) return;
    const schedule = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : (cb: FrameRequestCallback) => window.setTimeout(() => cb(0), 16);
    scrollFrameRef.current = schedule(() => {
      scrollFrameRef.current = null;
      if (scrollerRef.current) setScrollLeft(scrollerRef.current.scrollLeft);
    }) as unknown as number;
  };

  // Keep the playhead on screen: page forward while playing, and jump to it
  // after an outside seek (a note clicked in a list).
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !follow || dragRef.current || scrubbingRef.current) return;
    const x = currentTime * pps;
    const left = scroller.scrollLeft;
    const width = viewportWidth;
    if (x < left || x > left + width - RIGHT_PAD) {
      scroller.scrollLeft = Math.max(0, x - width * (playing ? 0.1 : 0.4));
      setScrollLeft(scroller.scrollLeft);
    }
  }, [currentTime, pps, follow, playing, viewportWidth]);

  // Forget optimistic positions once the parent's data catches up (or after 8s).
  useEffect(() => {
    setPending((previous) => {
      const ids = Object.keys(previous);
      if (ids.length === 0) return previous;
      const next: Record<string, Pending> = {};
      const now = Date.now();
      for (const id of ids) {
        const entry = previous[id];
        let item: TimelineItem | undefined;
        for (const lane of lanes) {
          item = lane.items.find((candidate) => candidate.id === id);
          if (item) break;
        }
        const caughtUp = item
          && Math.abs(item.start - entry.start) < 1e-3
          && Math.abs((itemEnd(item) ?? -1) - (entry.end ?? -1)) < 1e-3;
        if (item && !caughtUp && now - entry.at < 8000) next[id] = entry;
      }
      return Object.keys(next).length === ids.length ? previous : next;
    });
  }, [lanes]);

  const timeAtClientX = useCallback((clientX: number) => {
    const inner = innerRef.current;
    if (!inner) return 0;
    const rect = inner.getBoundingClientRect();
    const { pps: current, effectiveDuration: total } = live.current;
    return pxToTime(clientX - rect.left, current, total);
  }, []);

  const handleScrubStart = useCallback((event: React.PointerEvent) => {
    if (event.button > 0 || !live.current.onSeek) return;
    const target = event.currentTarget as HTMLElement;
    scrubbingRef.current = true;
    try {
      target.setPointerCapture?.(event.pointerId);
    } catch {
      /* not capturable (synthetic event) */
    }
    live.current.onSeek?.(timeAtClientX(event.clientX));
  }, [timeAtClientX]);

  const handleScrubMove = useCallback((event: React.PointerEvent) => {
    if (!scrubbingRef.current) return;
    live.current.onSeek?.(timeAtClientX(event.clientX));
  }, [timeAtClientX]);

  const handleScrubEnd = useCallback((event: React.PointerEvent) => {
    if (!scrubbingRef.current) return;
    scrubbingRef.current = false;
    try {
      (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
    } catch {
      /* already released */
    }
  }, []);

  const commitChange = useCallback((item: TimelineItem, lane: TimelineLane, next: TimelineItemChange) => {
    const handler = live.current.onItemChange;
    if (!handler) return;
    setPending((previous) => ({ ...previous, [item.id]: { start: next.start, end: next.end, at: Date.now() } }));
    Promise.resolve()
      .then(() => handler(item, lane, next))
      .catch(() => {
        setPending((previous) => {
          const { [item.id]: _dropped, ...rest } = previous;
          return rest;
        });
      });
  }, []);

  const snapCandidates = useCallback((excludeId: string) => {
    const { lanes: allLanes, currentTime: now, inPoint: markIn, outPoint: markOut, effectiveDuration: total } = live.current;
    const points: number[] = [0, total, now];
    if (typeof markIn === 'number') points.push(markIn);
    if (typeof markOut === 'number') points.push(markOut);
    for (const lane of allLanes) {
      for (const candidate of lane.items) {
        if (candidate.id === excludeId) continue;
        points.push(candidate.start);
        const end = itemEnd(candidate);
        if (end !== null) points.push(end);
      }
    }
    return points;
  }, []);

  const handleItemPointerDown = useCallback((event: React.PointerEvent, item: TimelineItem, lane: TimelineLane, mode: DragMode) => {
    event.stopPropagation();
    if (event.button > 0) return;
    const canDrag = Boolean(lane.editable && item.editable !== false && live.current.onItemChange);
    if (!canDrag) return;
    const end = itemEnd(item);
    const isPoint = end === null;
    const original = { start: item.start, end: end ?? item.start };
    const state: DragState = {
      itemId: item.id,
      laneId: lane.id,
      mode: isPoint ? 'move' : mode,
      startX: event.clientX,
      original,
      isPoint,
      preview: original,
      moved: false,
    };
    dragRef.current = state;
    const candidates = snapCandidates(item.id);
    const minDuration = 1 / fps;

    const onMove = (moveEvent: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;
      const dx = moveEvent.clientX - current.startX;
      if (!current.moved && Math.abs(dx) < 3) return;
      const { pps: scale, effectiveDuration: total } = live.current;
      let next = applyDrag(current.mode, current.original, dx / scale, total, minDuration);
      if (current.mode === 'move') {
        const snapped = snapTime(next.start, candidates, scale, fps);
        next = applyDrag('move', current.original, snapped - current.original.start, total, minDuration);
      } else if (current.mode === 'resize-start') {
        next = { start: Math.min(snapTime(next.start, candidates, scale, fps), next.end - minDuration), end: next.end };
        next.start = Math.max(0, next.start);
      } else {
        next = { start: next.start, end: Math.max(snapTime(next.end, candidates, scale, fps), next.start + minDuration) };
        if (total > 0) next.end = Math.min(total, next.end);
      }
      const updated = { ...current, preview: next, moved: true };
      dragRef.current = updated;
      setDrag(updated);
    };

    const finish = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      const current = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!current || !current.moved) return;
      justDraggedRef.current = true;
      window.setTimeout(() => { justDraggedRef.current = false; }, 0);
      const changed =
        Math.abs(current.preview.start - current.original.start) > 1e-4 ||
        Math.abs(current.preview.end - current.original.end) > 1e-4;
      if (!changed) return;
      commitChange(item, lane, {
        start: current.preview.start,
        end: current.isPoint ? null : current.preview.end,
      });
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  }, [commitChange, fps, snapCandidates]);

  const handleItemClick = useCallback((event: React.MouseEvent, item: TimelineItem, lane: TimelineLane) => {
    event.stopPropagation();
    if (justDraggedRef.current) return;
    onItemSelect?.(item, lane);
    live.current.onSeek?.(item.start);
  }, [onItemSelect]);

  const handleItemDoubleClick = useCallback((item: TimelineItem, lane: TimelineLane) => {
    onItemActivate?.(item, lane);
  }, [onItemActivate]);

  const handleItemContextMenu = useCallback((event: React.MouseEvent, item: TimelineItem, lane: TimelineLane) => {
    if (!onItemContextMenu) return;
    event.preventDefault();
    event.stopPropagation();
    onItemContextMenu(item, lane, event);
  }, [onItemContextMenu]);

  const handleItemKeyDown = useCallback((event: React.KeyboardEvent, item: TimelineItem, lane: TimelineLane) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onItemSelect?.(item, lane);
      live.current.onSeek?.(item.start);
      return;
    }
    // Alt + arrows nudge an editable item by a frame (Shift: a second).
    const canNudge = lane.editable && item.editable !== false && live.current.onItemChange;
    if (canNudge && event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      const step = (event.shiftKey ? 1 : 1 / fps) * (event.key === 'ArrowLeft' ? -1 : 1);
      const end = itemEnd(item);
      const moved = applyDrag('move', { start: item.start, end: end ?? item.start }, step, live.current.effectiveDuration, 1 / fps);
      commitChange(item, lane, { start: moved.start, end: end === null ? null : moved.end });
    }
  }, [commitChange, fps, onItemSelect]);

  const handleItemHover = useCallback((item: TimelineItem | null, lane: TimelineLane | null, anchor: HTMLElement | null) => {
    setHover(item && lane && anchor ? { item, lane, anchor } : null);
  }, []);

  const handleRulerKeyDown = (event: React.KeyboardEvent) => {
    if (!onSeek) return;
    let next: number | null = null;
    const frame = 1 / fps;
    if (event.key === 'ArrowLeft') next = currentTime - (event.shiftKey ? 1 : frame);
    else if (event.key === 'ArrowRight') next = currentTime + (event.shiftKey ? 1 : frame);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = effectiveDuration;
    if (next === null) return;
    event.preventDefault();
    event.stopPropagation();
    onSeek(Math.min(effectiveDuration, Math.max(0, next)));
  };

  // Per-lane row packing at the current zoom.
  const laneLayouts = useMemo(() => lanes.map((lane) => {
    const kind: TimelineLaneKind = lane.kind ?? 'markers';
    const { rows, rowCount } = assignRows(
      lane.items.map((item) => ({ start: item.start, end: itemEnd(item) })),
      pps,
      { maxRows: lane.maxRows ?? DEFAULT_MAX_ROWS[kind], pointWidthPx: 14, minBarPx: 6, gapPx: 2 }
    );
    const height = Math.max(MIN_LANE_HEIGHT, LANE_PAD * 2 + rowCount * ROW_HEIGHT[kind] + (rowCount - 1) * ROW_GAP);
    return { lane, kind, rows, height };
  }), [lanes, pps]);

  const spacing = useMemo(() => chooseTickSpacing(pps, fps), [pps, fps]);
  const viewStart = Math.max(0, (scrollLeft - VIRTUAL_BUFFER_PX) / pps);
  const viewEnd = (scrollLeft + viewportWidth + VIRTUAL_BUFFER_PX) / pps;
  const ticks = useMemo(
    () => computeTicks(viewStart, viewEnd, effectiveDuration, spacing),
    [viewStart, viewEnd, effectiveDuration, spacing]
  );
  const playheadX = Math.min(effectiveDuration, Math.max(0, currentTime)) * pps;
  const hasMarks = typeof inPoint === 'number' || typeof outPoint === 'number';
  const shadeStart = typeof inPoint === 'number' ? inPoint : 0;
  const shadeEnd = typeof outPoint === 'number' ? outPoint : effectiveDuration;
  const zoomFactor = fitPps > 0 ? pps / fitPps : 1;
  const hasItems = lanes.some((lane) => lane.items.length > 0);
  const showEmpty = effectiveDuration <= 0;

  return (
    <Box
      data-testid={testId}
      sx={{
        position: 'relative',
        border: `1px solid ${brand.border}`,
        borderRadius: 3,
        bgcolor: studioTimeline.panel,
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          minHeight: 42,
          borderBottom: `1px solid ${brand.border}`,
          bgcolor: brand.ivory,
          flexWrap: 'wrap',
        }}
      >
        {title && (
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mr: 0.5 }}>
            {title}
          </Typography>
        )}
        {toolbarExtras}
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          data-testid="timeline-timecode"
          sx={{ fontFamily: TIMECODE_FONT, fontVariantNumeric: 'tabular-nums', color: 'text.secondary', mr: 1, whiteSpace: 'nowrap' }}
        >
          {formatTimecode(currentTime, fps)} / {formatTimecode(effectiveDuration, fps)}
        </Typography>
        <Tooltip title="Zoom out (−)">
          <span>
            <IconButton size="small" aria-label="Zoom out" onClick={() => zoomTo(pps / ZOOM_STEP)} disabled={showEmpty || fitMode}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" sx={{ minWidth: 34, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
          {fitMode ? 'Fit' : `${zoomFactor < 10 ? zoomFactor.toFixed(1) : Math.round(zoomFactor)}×`}
        </Typography>
        <Tooltip title="Zoom in (+)">
          <span>
            <IconButton size="small" aria-label="Zoom in" onClick={() => zoomTo(pps * ZOOM_STEP)} disabled={showEmpty}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Fit the whole video (\)">
          <span>
            <IconButton size="small" aria-label="Zoom to fit" onClick={fit} disabled={showEmpty || fitMode}>
              <FitScreenIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={follow ? 'Following the playhead' : 'Follow the playhead'}>
          <IconButton
            size="small"
            aria-label="Follow playhead"
            aria-pressed={follow}
            onClick={() => setFollow((value) => !value)}
            sx={{ color: follow ? 'primary.main' : 'text.secondary' }}
          >
            <MyLocationIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {showEmpty ? (
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage || 'The timeline appears once the video’s length is known.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex' }}>
          {/* Lane labels */}
          <Box sx={{ width: { xs: GUTTER_WIDTH_PHONE, sm: GUTTER_WIDTH }, flexShrink: 0, borderRight: `1px solid ${brand.border}`, bgcolor: studioTimeline.gutter }}>
            <Box sx={{ height: RULER_HEIGHT, borderBottom: `1px solid ${brand.border}`, display: 'flex', alignItems: 'center', px: 1.25 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                {`${formatFrameRate(fps)} fps`}
              </Typography>
            </Box>
            {laneLayouts.map(({ lane, height }) => (
              <Box
                key={lane.id}
                sx={{
                  height,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  borderBottom: `1px solid ${brand.border}`,
                  color: 'text.secondary',
                  '& svg': { fontSize: 16 },
                }}
              >
                {lane.icon}
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {lane.label}
                </Typography>
                <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                  {lane.items.length}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Scrolling content */}
          <Box
            ref={scrollerRef}
            onScroll={handleScroll}
            sx={{ position: 'relative', flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden' }}
          >
            <Box
              ref={innerRef}
              onPointerMove={handleScrubMove}
              onPointerUp={handleScrubEnd}
              onPointerCancel={handleScrubEnd}
              sx={{ position: 'relative', width: contentWidth }}
            >
              {/* Ruler */}
              <Box
                role="slider"
                tabIndex={0}
                aria-label="Playhead position"
                aria-valuemin={0}
                aria-valuemax={Math.round(effectiveDuration * 100) / 100}
                aria-valuenow={Math.round(currentTime * 100) / 100}
                aria-valuetext={formatTimecode(currentTime, fps)}
                onPointerDown={handleScrubStart}
                onKeyDown={handleRulerKeyDown}
                sx={{
                  position: 'relative',
                  height: RULER_HEIGHT,
                  borderBottom: `1px solid ${brand.border}`,
                  bgcolor: studioTimeline.gutter,
                  cursor: 'ew-resize',
                  touchAction: 'none',
                  '&:focus-visible': { outline: `2px solid ${brand.ink}`, outlineOffset: -2 },
                }}
              >
                {ticks.map((tick) => {
                  const label = tick.major ? formatRulerLabel(tick.time, fps, spacing.major) : null;
                  // A label that would be cut off at the end of the ruler is left out.
                  const showLabel = label !== null && tick.time * pps + 4 + label.length * RULER_CHAR_PX <= contentWidth;
                  return (
                    <Box
                      key={tick.time}
                      sx={{
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        transform: `translateX(${tick.time * pps}px)`,
                        width: '1px',
                        height: tick.major ? 12 : 6,
                        bgcolor: tick.major ? studioTimeline.tickMajor : studioTimeline.tickMinor,
                        pointerEvents: 'none',
                      }}
                    >
                      {showLabel && (
                        <Typography
                          component="span"
                          sx={{
                            position: 'absolute',
                            left: 4,
                            bottom: 9,
                            fontSize: 10.5,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                            color: 'text.secondary',
                            fontVariantNumeric: 'tabular-nums',
                            fontFamily: TIMECODE_FONT,
                          }}
                        >
                          {label}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
                {typeof inPoint === 'number' && (
                  <Box aria-hidden="true" sx={{ position: 'absolute', top: 0, bottom: 0, left: inPoint * pps, width: 6, borderLeft: `2px solid ${brand.copper}`, borderTop: `2px solid ${brand.copper}`, borderBottom: `2px solid ${brand.copper}`, pointerEvents: 'none' }} />
                )}
                {typeof outPoint === 'number' && (
                  <Box aria-hidden="true" sx={{ position: 'absolute', top: 0, bottom: 0, left: outPoint * pps - 6, width: 6, borderRight: `2px solid ${brand.copper}`, borderTop: `2px solid ${brand.copper}`, borderBottom: `2px solid ${brand.copper}`, pointerEvents: 'none' }} />
                )}
              </Box>

              {/* Lanes */}
              {laneLayouts.map(({ lane, kind, rows, height }) => (
                <LaneTrack
                  key={lane.id}
                  lane={lane}
                  kind={kind}
                  height={height}
                  rows={rows}
                  pps={pps}
                  viewStart={viewStart}
                  viewEnd={viewEnd}
                  fps={fps}
                  selectedItemId={selectedItemId}
                  dragPreview={drag && drag.laneId === lane.id ? drag : null}
                  pending={pending}
                  canDrag={Boolean(lane.editable && onItemChange)}
                  onItemPointerDown={handleItemPointerDown}
                  onItemClick={handleItemClick}
                  onItemDoubleClick={handleItemDoubleClick}
                  onItemContextMenu={handleItemContextMenu}
                  onItemKeyDown={handleItemKeyDown}
                  onItemHover={handleItemHover}
                  onBackgroundPointerDown={handleScrubStart}
                />
              ))}

              {!hasItems && !lanes.some((lane) => lane.emptyText) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ position: 'absolute', left: scrollLeft + 12, bottom: 6, pointerEvents: 'none' }}
                >
                  {emptyMessage || 'Nothing on the timeline yet.'}
                </Typography>
              )}

              {/* In/out shading */}
              {hasMarks && shadeEnd > shadeStart && (
                <Box
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    top: RULER_HEIGHT,
                    bottom: 0,
                    left: shadeStart * pps,
                    width: (shadeEnd - shadeStart) * pps,
                    bgcolor: withAlpha(brand.copper, 0.07),
                    borderLeft: typeof inPoint === 'number' ? `1px dashed ${withAlpha(brand.copper, 0.7)}` : 'none',
                    borderRight: typeof outPoint === 'number' ? `1px dashed ${withAlpha(brand.copper, 0.7)}` : 'none',
                    pointerEvents: 'none',
                  }}
                />
              )}

              {/* Playhead */}
              <Box
                aria-hidden="true"
                data-testid="timeline-playhead"
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: 2,
                  transform: `translateX(${playheadX - 1}px)`,
                  bgcolor: PLAYHEAD_COLOR,
                  pointerEvents: 'none',
                  zIndex: 6,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: -5,
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: `8px solid ${PLAYHEAD_COLOR}`,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Popper
        open={Boolean(hover) && !drag}
        anchorEl={hover?.anchor ?? null}
        placement="top"
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        sx={{ zIndex: 1500, pointerEvents: 'none' }}
      >
        {hover && (
          <Paper
            elevation={0}
            sx={{
              px: 1.25,
              py: 1,
              maxWidth: 320,
              bgcolor: studioTimeline.popover,
              color: studioTimeline.popoverText,
              borderRadius: 2,
              boxShadow: studioTimeline.popoverShadow,
            }}
          >
            <Typography sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', opacity: 0.75, mb: 0.25 }}>
              {hover.lane.label} · {describeRange(hover.item, fps)}
            </Typography>
            {hover.item.tooltip ?? (
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}>{hover.item.label}</Typography>
            )}
          </Paper>
        )}
      </Popper>
    </Box>
  );
});

export default ProTimeline;
